import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";

import { toast } from "react-toastify";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";

import { getTheme } from "./tickets/theme";
import TopBar from "./tickets/TopBar";
import TicketPanel from "./tickets/TicketPanel";
import Conversation from "./tickets/Conversation";
import CrmSidebar from "./tickets/CrmSidebar";
import QrModal from "./tickets/QrModal";
import SettingsMenu from "../CrmKanban/kanban/SettingsMenu";

const SETTINGS_KEY = "crmTicketsSettings";
const DEFAULT_SETTINGS = {
  variant: "funnel",
  densidade: "rico",
  slaLimite: 60,
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

// Injeta as 3 fontes do Google uma única vez (mesma abordagem do Kanban).
const ensureFonts = () => {
  const id = "crm-tickets-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800;900&family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap";
  document.head.appendChild(link);
};

const CrmTickets = () => {
  const { user } = useContext(AuthContext);
  const { whatsApps } = useWhatsApps();

  // Embed (aba do ERP): preenche a altura toda.
  const embed = sessionStorage.getItem("whaticketEmbed") === "1";

  const [settings, setSettings] = useState(loadSettings);
  const [settingsAnchor, setSettingsAnchor] = useState(null);

  const [tab, setTab] = useState("inbox"); // inbox | resolvidos | busca
  const [sub, setSub] = useState("open"); // open | pending
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [queueFilter, setQueueFilter] = useState(""); // "" = todas | id única
  const [whatsappFilter, setWhatsappFilter] = useState(""); // "" = todas | id da conexão

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [qrOpen, setQrOpen] = useState(false);

  const [attendants, setAttendants] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [stages, setStages] = useState([]);
  const [leadsByNumber, setLeadsByNumber] = useState({});
  const [blockGroups, setBlockGroups] = useState(false);

  const [, setTick] = useState(0);

  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* localStorage indisponível */
    }
  }, [settings]);

  // Aviso in-app de agendamento (criação/lembrete) para o vendedor dono.
  useEffect(() => {
    if (!user?.id) return;
    const socket = openSocket();
    const handleAppointment = (data) => {
      const isMine = data?.appointment?.userId === user.id;
      const relevant = data?.action === "reminder" || data?.action === "create";
      if (!isMine || !relevant) return;
      const name = data.appointment?.contact?.name || "contato";
      let hour = "";
      try {
        hour = format(new Date(data.appointment.scheduledAt), "HH:mm");
      } catch {
        hour = "";
      }
      toast.info(`Agendamento: ${name}${hour ? ` às ${hour}` : ""}`);
    };
    socket.on("appointment", handleAppointment);
    return () => {
      socket.disconnect();
    };
  }, [user?.id]);

  // Tick de 30s para atualizar timers/SLA client-side.
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  // Listas auxiliares (atendentes, filas, estágios).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/");
        setAttendants(data?.users || []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data } = await api.get("/queue");
        setAllQueues(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data } = await api.get("/crm/stages");
        setStages(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data } = await api.get("/crm/settings");
        setBlockGroups(Boolean(data?.blockGroups));
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const V = getTheme(settings.variant);

  // Filas do usuário (para o topbar e o select do painel).
  const userQueues = useMemo(() => user?.queues || [], [user]);

  const queueIds = useMemo(() => {
    if (queueFilter) return [Number(queueFilter)];
    return userQueues.map((q) => q.id);
  }, [queueFilter, userQueues]);

  const queuesLabel = useMemo(() => {
    if (queueFilter) {
      const q = userQueues.find((x) => String(x.id) === String(queueFilter));
      return q ? q.name : "";
    }
    return userQueues.map((q) => q.name).join(" · ");
  }, [queueFilter, userQueues]);

  // Conexão: verde se há algum whatsapp CONNECTED.
  const connectedWhats = useMemo(
    () => (whatsApps || []).find((w) => w.status === "CONNECTED"),
    [whatsApps],
  );
  const connected = Boolean(connectedWhats);

  // Whatsapp alvo do modal QR: o do ticket selecionado, senão o primeiro.
  const targetWhats = useMemo(() => {
    if (selectedTicket?.whatsappId) {
      const w = (whatsApps || []).find(
        (x) => x.id === selectedTicket.whatsappId,
      );
      if (w) return w;
    }
    return (whatsApps || [])[0] || null;
  }, [selectedTicket, whatsApps]);

  // Whatsapp do ticket selecionado (read-only na sidebar/ATENDIMENTO).
  const ticketWhats = useMemo(() => {
    if (!selectedTicket?.whatsappId) return connectedWhats || null;
    return (
      (whatsApps || []).find((x) => x.id === selectedTicket.whatsappId) || null
    );
  }, [selectedTicket, whatsApps, connectedWhats]);

  // Carrega o lead (find-or-create) pelo número ao selecionar — alimenta chips
  // (tag/canal) na lista e no header sem bloquear a lista.
  const ensureLead = useCallback(
    async (ticket) => {
      const number = ticket?.contact?.number;
      if (!number || leadsByNumber[number]) return;
      try {
        const { data } = await api.get(`/crm/leads/by-number/${number}`);
        setLeadsByNumber((prev) => ({ ...prev, [number]: data }));
      } catch (err) {
        // Silencioso: a lista funciona sem tag/canal.
      }
    },
    [leadsByNumber],
  );

  const handleSelectTicket = useCallback(
    (ticket) => {
      setSelectedTicket(ticket);
      ensureLead(ticket);
    },
    [ensureLead],
  );

  const handleAcceptTicket = useCallback(
    async (ticket) => {
      try {
        await api.put(`/tickets/${ticket.id}`, {
          status: "open",
          userId: user?.id,
        });
        setTab("inbox");
        setSub("open");
        setSelectedTicket({ ...ticket, status: "open", userId: user?.id });
        ensureLead(ticket);
      } catch (err) {
        toastError(err);
      }
    },
    [user, ensureLead],
  );

  const selectedLead = selectedTicket
    ? leadsByNumber[selectedTicket.contact?.number]
    : null;

  const openSettings = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSettingsAnchor({ top: r.bottom + 4, left: r.right });
  };

  return (
    <div
      style={{
        // Embed roda em iframe: "100%" depende da cadeia html/body ter altura
        // definida — quando não tem, o root nunca estoura e o overflow interno
        // (scroll infinito da lista) não dispara. 100dvh mede a viewport do
        // próprio iframe, independente do body.
        height: embed ? "100dvh" : "calc(100vh - 48px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: V.pageBg,
        fontFamily: V.font,
        position: "relative",
      }}
    >
      <TopBar
        V={V}
        connected={connected}
        queuesLabel={queuesLabel}
        userName={user?.name}
        onOpenSettings={openSettings}
      />

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <TicketPanel
          V={V}
          settings={settings}
          blockGroups={blockGroups}
          tab={tab}
          onTabChange={setTab}
          sub={sub}
          onSubChange={setSub}
          search={search}
          onSearchChange={setSearch}
          showAll={showAll}
          onToggleShowAll={() => setShowAll((s) => !s)}
          queueIds={queueIds}
          userQueues={userQueues}
          onQueueChange={setQueueFilter}
          whatsappId={whatsappFilter ? Number(whatsappFilter) : null}
          whatsApps={whatsApps || []}
          onWhatsappChange={setWhatsappFilter}
          selectedTicketId={selectedTicket?.id}
          leadsByNumber={leadsByNumber}
          onSelectTicket={handleSelectTicket}
          onAcceptTicket={handleAcceptTicket}
          onNewTicket={() => {
            /* criação de ticket usa o fluxo padrão do Whaticket (fora de escopo) */
          }}
        />

        <div
          style={{
            flex: 1,
            display: "flex",
            background: V.chatBg,
            minWidth: 0,
            position: "relative",
          }}
        >
          <Conversation
            V={V}
            ticket={selectedTicket}
            lead={selectedLead}
            slaLimite={settings.slaLimite}
            connected={connected}
            onOpenQr={() => setQrOpen(true)}
          />

          {selectedTicket && (
            <CrmSidebar
              V={V}
              ticket={selectedTicket}
              whatsapp={ticketWhats}
              attendants={attendants}
              queues={{ list: allQueues, stages }}
              open={sidebarOpen}
              onToggle={() => setSidebarOpen((s) => !s)}
              onLeadChange={(lead) => {
                const key = selectedTicket.contact?.number;
                if (key) {
                  setLeadsByNumber((prev) => ({ ...prev, [key]: lead }));
                }
              }}
            />
          )}

          <QrModal
            V={V}
            open={qrOpen}
            whatsapp={targetWhats}
            onClose={() => setQrOpen(false)}
          />
        </div>
      </div>

      <SettingsMenu
        anchorPosition={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={() => setSettingsAnchor(null)}
        settings={settings}
        onChange={setSettings}
      />
    </div>
  );
};

export default CrmTickets;
